import React, { useState, useEffect } from 'react';
import { ShoppingCart, Star, Loader2, Shield, Zap, Headphones, Apple, Monitor } from 'lucide-react';
import { productsAPI } from '../services/api';
import { toast } from 'sonner';
import { useGeo } from '../contexts/GeoContext';
import ScrollReveal from '../components/ScrollReveal';
import Feedbacks from '../components/Feedbacks';
import { useTranslation, trProductName, trProductDesc, trPlanType } from '../utils/translations';
import { get_payment_link, get_pix_data } from '../utils/pixCodes';
import { cartStorage } from '../utils/cart';
import { useNavigate } from 'react-router-dom';
import DeviceIntro from '../components/DeviceIntro';

const ProductCard = ({ product, delay, getPrice, getOriginalPrice, getPlanCurrency, onBuy, tr, isBrazil }) => (
  <ScrollReveal direction="up" delay={delay} scale>
    <div className="bg-gray-900/40 rounded-2xl overflow-hidden border border-gray-800/70 hover:border-red-500/40 transition-all" data-testid={`product-card-${product.id}`}>
      <div className="flex flex-col md:grid md:grid-cols-5 gap-0">
        <div className="md:col-span-2 relative bg-gray-950/60 p-5 sm:p-7 flex items-center justify-center">
          {product.popular && (
            <div className="absolute top-3 left-3 sm:top-4 sm:left-4 bg-amber-500 text-black font-bold px-3 py-1 rounded-full text-[10px] sm:text-xs z-10 flex items-center gap-1">
              <Star className="w-3 h-3" fill="currentColor" /> {isBrazil ? 'MAIS ESCOLHIDO' : 'BEST SELLER'}
            </div>
          )}
          {product.video ? (
            <div className="w-full max-w-[300px] md:max-w-none">
              <div className="relative rounded-xl overflow-hidden border border-white/5">
                <video controls playsInline preload="metadata" poster={product.image} className="w-full h-auto rounded-xl" data-testid={`product-video-${product.id}`}>
                  <source src={product.video} type="video/mp4" />
                </video>
              </div>
              <p className="text-center text-gray-500 text-[10px] sm:text-xs font-medium tracking-wide mt-2 uppercase">{isBrazil ? 'Video demonstracao' : 'Demo video'}</p>
            </div>
          ) : (
            <img src={product.image} alt={product.name} className="w-full max-w-[260px] md:max-w-none h-auto rounded-xl" />
          )}
        </div>
        <div className="md:col-span-3 p-5 sm:p-8">
          <h2 className="text-lg sm:text-2xl font-bold text-white mb-1.5">{trProductName(isBrazil, product.id, product.name)}</h2>
          <p className="text-gray-500 text-xs sm:text-sm mb-5 line-clamp-2">{trProductDesc(isBrazil, product.id, product.description)}</p>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {product.plans.filter(plan => isBrazil || plan.type !== '15 Dias').map((plan, i, arr) => {
              const displayPrice = getPrice(plan.id, plan.price);
              const displayOriginal = getOriginalPrice(plan.id, plan.original_price || plan.price);
              const planCurrency = getPlanCurrency(plan.id);
              const discount = displayOriginal ? Math.round(((displayOriginal - displayPrice) / displayOriginal) * 100) : 0;
              const isLast = i === arr.length - 1;
              return (
                <button key={plan.id} onClick={() => onBuy(plan.id)} className={`relative overflow-hidden rounded-xl text-left transition-all group active:scale-[0.97] ${isLast ? 'bg-red-950/20 border border-red-500/30 hover:border-red-500/60' : 'bg-gray-900/40 border border-gray-800/60 hover:border-gray-700'}`} data-testid={`plan-card-${plan.id}`}>
                  {discount > 0 && <div className="absolute top-0 right-0 bg-red-600/90 text-white text-[9px] sm:text-[10px] font-bold px-2 py-1 rounded-bl-lg">-{discount}%</div>}
                  <div className="p-3.5 sm:p-4">
                    <p className="text-gray-500 text-[10px] sm:text-xs font-medium uppercase tracking-wider mb-1">{trPlanType(isBrazil, plan.type)}</p>
                    {displayOriginal > displayPrice && <p className="text-gray-600 text-[10px] sm:text-xs line-through">{planCurrency} {displayOriginal.toFixed(2)}</p>}
                    <p className="text-white font-bold text-lg sm:text-xl mb-3" data-testid={`price-${plan.id}`}>{planCurrency} {displayPrice.toFixed(2)}</p>
                    <div className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all ${isLast ? 'bg-red-600 text-white group-hover:bg-red-500' : 'bg-white/5 text-gray-300 group-hover:bg-red-600 group-hover:text-white border border-gray-800 group-hover:border-red-600'}`}>
                      <ShoppingCart className="w-3.5 h-3.5" /><span>{tr('buy')}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {product.plans.some(p => p.type === 'Permanente') && (
            <p className="text-[10px] sm:text-xs text-gray-500 mt-4 leading-relaxed">
              <span className="text-red-500 font-bold">{isBrazil ? 'PERMANENTE' : 'LIFETIME'}:</span>{' '}
              {isBrazil
                ? 'Voce compra somente uma vez e recebe todas as atualizacoes futuras de graca, sem mensalidade.'
                : 'Pay once and get all future updates for free, no monthly fees.'}
            </p>
          )}
        </div>
      </div>
    </div>
  </ScrollReveal>
);

const AndroidIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24a11.46 11.46 0 00-8.94 0L5.65 5.67c-.19-.28-.54-.37-.83-.22-.3.16-.42.54-.26.85L6.4 9.48A10.81 10.81 0 001 18h22a10.81 10.81 0 00-5.4-8.52zM7 15.25a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5zm10 0a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5z"/></svg>
);

const CATEGORIES = [
  { key: 'android', label: 'ANDROID', sub: null, ids: ['2'], Icon: AndroidIcon },
  { key: 'ios', label: 'iOS', sub: null, ids: ['3'], Icon: Apple },
  { key: 'emulador', label: 'EMULADOR', sub: '+ BYPASS', ids: ['1', '5', '6'], Icon: Monitor },
];

const PlatformSelector = ({ active, onSelect, isBrazil }) => (
  <div className="grid grid-cols-3 gap-2 sm:gap-3 p-1.5 rounded-2xl bg-gray-900/70 border border-gray-800" data-testid="platform-selector">
    {CATEGORIES.map((cat) => {
      const isActive = active === cat.key;
      const label = cat.key === 'emulador' && !isBrazil ? 'EMULATOR' : cat.label;
      return (
        <button
          key={cat.key}
          onClick={() => onSelect(cat.key)}
          className={`relative flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2.5 py-3.5 sm:py-4 px-2 rounded-xl font-bold transition-all duration-300 active:scale-[0.97] ${
            isActive
              ? 'bg-gradient-to-b from-red-600 to-red-700 text-white shadow-lg shadow-red-900/50'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
          data-testid={`category-card-${cat.key}`}
        >
          <cat.Icon className={`w-6 h-6 sm:w-6 sm:h-6 ${isActive ? 'text-white' : 'text-red-500'}`} style={isActive ? {} : { filter: 'drop-shadow(0 0 8px rgba(239,68,68,0.5))' }} />
          <span className="flex flex-col items-center sm:items-start leading-tight">
            <span className="text-xs sm:text-base gaming-title tracking-wide">{label}</span>
            {cat.sub && <span className={`text-[8px] sm:text-[10px] tracking-widest ${isActive ? 'text-red-200' : 'text-red-500'}`}>{cat.sub}</span>}
          </span>
        </button>
      );
    })}
  </div>
);

const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('android');
  const [showIntro, setShowIntro] = useState(true);
  const { currency, getPrice, getOriginalPrice, isBrazil, getIntlLink, getPlanCurrency } = useGeo();
  const tr = useTranslation(isBrazil);
  const navigate = useNavigate();

  useEffect(() => { loadProducts(); }, []);

  const handleIntroSelect = (key) => {
    setCategory(key);
    setShowIntro(false);
    setTimeout(() => {
      document.getElementById('category-products-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const loadProducts = async () => {
    try {
      const data = await productsAPI.getAll();
      setProducts(data.filter(p => p.id !== '4').sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (e) { toast.error('Erro ao carregar produtos'); }
    finally { setLoading(false); }
  };

  const handleBuy = (planId) => {
    const pixData = get_pix_data(planId);
    const productId = planId.split('-')[0];
    if (pixData?.code) {
      cartStorage.clearCart();
      const data = pixData;
      const prod = products.find(p => p.plans.some(pl => pl.id === planId));
      const plan = products.flatMap(p => p.plans).find(pl => pl.id === planId);
      cartStorage.addItem({
        productId: planId.split('-')[0],
        productName: trProductName(isBrazil, prod?.id, prod?.name || ''),
        planId: planId,
        planType: trPlanType(isBrazil, plan?.type || ''),
        price: getPrice(planId, data.price),
        currency: currency,
        timestamp: Date.now()
      });
      navigate('/checkout');
    } else if (!isBrazil && (productId === '2' || productId === '3')) {
      // Android/iOS internacional: vai pro checkout (PurinCash, conversao direta R$->US$)
      cartStorage.clearCart();
      const prod = products.find(p => p.plans.some(pl => pl.id === planId));
      const plan = products.flatMap(p => p.plans).find(pl => pl.id === planId);
      cartStorage.addItem({
        productId,
        productName: trProductName(isBrazil, prod?.id, prod?.name || ''),
        planId: planId,
        planType: trPlanType(isBrazil, plan?.type || ''),
        price: getPrice(planId, plan?.price || 0),
        currency: currency,
        timestamp: Date.now()
      });
      navigate('/checkout');
    } else {
      // Produtos PerfectPay (Emulador/Bypass/Aimless): internacional vai pra Centerpag
      if (!isBrazil) {
        const intlLink = getIntlLink(planId);
        if (intlLink) { window.open(intlLink, '_blank'); return; }
      }
      const link = get_payment_link(planId);
      if (link) { window.open(link, '_blank'); }
      else { toast.info(isBrazil ? 'Em breve! Chame nosso suporte para comprar' : 'Coming soon! Contact our support to buy'); }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-red-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-20 sm:pb-8 relative overflow-hidden">
      {showIntro && <DeviceIntro isBrazil={isBrazil} onSelect={handleIntroSelect} />}
      {/* Hero compact */}
      <section className="relative pt-10 sm:pt-16 pb-8 sm:pb-12 px-4 sm:px-6">
        <div className="absolute inset-0 bg-black" />

        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <ScrollReveal direction="down" scale>
            <div className="logo-flash-wrap mx-auto mb-4 sm:mb-5" data-testid="hero-logo-wrap">
              <img
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_07e3fa1c-accb-40af-b9f6-b18f873b4bec/artifacts/3s7e8l1h_logodas.png"
                alt="Cloud Cheats"
                className="logo-flash-img h-16 sm:h-24 md:h-32 w-auto object-contain mx-auto"
              />
            </div>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={100}>
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-black tracking-tight" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              CLOUD <span className="text-red-600">CHEATS</span>
            </h1>
            <p className="text-gray-500 text-sm sm:text-base mt-3 max-w-lg mx-auto">
              {isBrazil ? 'O melhor painel para dominar o Free Fire' : 'The best panel to dominate Free Fire'}
            </p>
          </ScrollReveal>

          {/* Stats inline */}
          <ScrollReveal direction="up" delay={200}>
            <div className="flex justify-center gap-8 sm:gap-12 mt-7 sm:mt-10">
              {[
                { val: '750+', label: isBrazil ? 'Clientes' : 'Clients' },
                { val: '24/7', label: isBrazil ? 'Suporte' : 'Support' },
                { val: '5.0', label: 'Rating' },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <p className="text-xl sm:text-2xl font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>{s.val}</p>
                  <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Products */}
      <section className="px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal direction="up">
            <div className="flex items-center justify-between mb-5 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white gaming-title">
                {isBrazil ? 'ESCOLHA SUA PLATAFORMA' : 'CHOOSE YOUR PLATFORM'}
              </h2>
              <button
                onClick={() => document.getElementById('feedbacks-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center gap-1.5 text-red-500 text-xs sm:text-sm font-bold hover:text-red-400 transition-colors"
                data-testid="scroll-feedbacks-btn"
              >
                <Star className="w-3.5 h-3.5" fill="currentColor" />
                {isBrazil ? 'VER RESULTADOS' : 'RESULTS'}
              </button>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={80}>
            <PlatformSelector active={category} onSelect={setCategory} isBrazil={isBrazil} />
          </ScrollReveal>

          <ScrollReveal direction="up" delay={120}>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-0 mt-5 sm:mt-6 mb-1 rounded-xl border border-gray-800/60 divide-y sm:divide-y-0 sm:divide-x divide-gray-800/60 overflow-hidden" data-testid="trust-info-banner">
              {[
                { icon: Zap, text: isBrazil ? 'Receba em menos de 5 segundos, por E-mail e WhatsApp' : 'Get it in under 5 seconds, via Email and WhatsApp' },
                { icon: Shield, text: isBrazil ? 'Compra 100% segura e protegida' : '100% safe and secure purchase' },
                { icon: Star, text: isBrazil ? 'Mais de 750 clientes ativos' : '750+ active customers' },
              ].map((item, i) => (
                <div key={i} className="flex-1 flex items-center gap-2.5 px-4 py-3 sm:py-4">
                  <item.icon className="w-4 h-4 text-red-500 shrink-0" />
                  <p className="text-[11px] sm:text-xs text-gray-400 leading-snug">{item.text}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>

          <div className="space-y-5 sm:space-y-8 mt-6 sm:mt-10" data-testid="category-products" key={category}>
            <div id="category-products-anchor" className="scroll-mt-20" />
            {products.filter(p => CATEGORIES.find(c => c.key === category)?.ids.includes(p.id)).map((product, idx) => (
              <ProductCard key={product.id} product={product} delay={idx * 80} getPrice={getPrice} getOriginalPrice={getOriginalPrice} getPlanCurrency={getPlanCurrency} onBuy={handleBuy} tr={tr} isBrazil={isBrazil} />
            ))}
          </div>
        </div>
      </section>

      {/* Gameplay video - YouTube showcase launcher */}
      <section className="px-3 sm:px-6 mt-16 sm:mt-24">
        <div className="max-w-3xl mx-auto">
          <ScrollReveal direction="up">
            <h2 className="text-xl sm:text-2xl font-bold text-white gaming-title mb-5 sm:mb-7 text-center">
              {isBrazil ? 'VEJA EM' : 'SEE IN'} <span className="text-red-600">{isBrazil ? 'ACAO' : 'ACTION'}</span>
            </h2>
          </ScrollReveal>
          <ScrollReveal direction="up" delay={100} scale>
            <a
              href="https://www.youtube.com/watch?v=842b_R83tHQ&t=780s"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex items-center gap-4 sm:gap-6 rounded-2xl overflow-hidden border border-white/10 hover:border-red-500/50 bg-gray-950 transition-all p-3 sm:p-4"
              data-testid="hero-watch-demo-button"
            >
              <div className="relative shrink-0 w-28 h-20 sm:w-40 sm:h-28 rounded-xl overflow-hidden bg-black">
                <img
                  src="https://img.youtube.com/vi/842b_R83tHQ/hqdefault.jpg"
                  alt="Gameplay demo thumbnail"
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white ml-0.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                  </div>
                </div>
              </div>
              <div className="text-left">
                <p className="text-white font-bold text-sm sm:text-lg gaming-title">
                  {isBrazil ? 'ASSISTIR GAMEPLAY' : 'WATCH GAMEPLAY'}
                </p>
                <p className="text-gray-500 text-xs sm:text-sm mt-1">
                  {isBrazil ? 'Veja o painel em acao no YouTube' : 'See the panel in action on YouTube'}
                </p>
              </div>
            </a>
          </ScrollReveal>
        </div>
      </section>

      {/* Features compact */}
      <section className="px-3 sm:px-6 mt-16 sm:mt-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-3 gap-3 sm:gap-6">
            {[
              { icon: Shield, title: isBrazil ? 'Anti-Ban' : 'Anti-Ban', desc: isBrazil ? 'Sistema avancado de protecao' : 'Advanced protection system' },
              { icon: Zap, title: isBrazil ? 'Atualizacoes' : 'Updates', desc: isBrazil ? 'Sempre atualizado' : 'Always updated' },
              { icon: Headphones, title: isBrazil ? 'Suporte 24/7' : 'Support 24/7', desc: isBrazil ? 'Equipe especializada' : 'Specialized team' },
            ].map((f, i) => (
              <ScrollReveal key={i} direction="up" delay={i * 80} scale>
                <div className="bg-gray-900/40 p-5 sm:p-7 rounded-xl border border-gray-800/40 text-center hover:border-red-500/30 transition-all">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-600/10 rounded-xl flex items-center justify-center mx-auto mb-2.5 sm:mb-4">
                    <f.icon className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
                  </div>
                  <p className="text-white font-bold text-xs sm:text-sm">{f.title}</p>
                  <p className="text-gray-500 text-[10px] sm:text-xs mt-1 hidden sm:block">{f.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Feedbacks */}
      <section className="px-3 sm:px-6 mt-16 sm:mt-24">
        <div className="max-w-7xl mx-auto">
          <Feedbacks isBrazil={isBrazil} />
        </div>
      </section>
    </div>
  );
};

export default Home;
